import { ScheduleConfigEventBlockType } from "@iventis/domain-model/model/scheduleConfigEventBlockType";
import { Node } from "@iventis/domain-model/model/node";
import { Content } from "@iventis/translations/content/typed-content";
import qs from "qs";
import { RadioGroup, FormControlLabel, Radio, Checkbox, Button } from "@mui/material";
import { StyledFieldLabel } from "@iventis/styles/src/components/forms";
import { labelMargin } from "@iventis/styles/src/atomic-rules";
import { IventisFilterOperator } from "@iventis/domain-model/model/iventisFilterOperator";
import React, { useState } from "react";
import { MicroService } from "@iventis/api-helpers";
import { AutocompleteWithLoading, FormWizardTemplate, Stages, LoadingComponent, ConfirmationDialogComponent } from "@iventis/components";
import { Theme } from "@emotion/react";
import { muiInputFormsCSS, styled } from "@iventis/styles";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { toast } from "@iventis/toasts/src/toast";
import { canEdit } from "@iventis/utilities";
import { getApiBaseUrl, scheduleApi } from "@iventis/api/src/api";

export const ImportEventBlocks: React.FC<{
    eventBlockTypes: ScheduleConfigEventBlockType[];
    rootScheduleVersionId: string;
    scheduleVersionNodeHierarchy: Node;
    defaultScheduleVersionId?: string;
    close: (refresh?: boolean) => void;
}> = ({ eventBlockTypes, scheduleVersionNodeHierarchy, defaultScheduleVersionId, close, rootScheduleVersionId }) => {
    const [errors, setErrors] = useState<string[]>();
    const [uploading, setUploading] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [eventBlockType, setEventBlockType] = useState<ScheduleConfigEventBlockType>(
        eventBlockTypes.find((ebt) => (defaultScheduleVersionId ? ebt.calendarLevel === 1 : ebt.calendarLevel == null))
    );
    const [scheduleVersion, setScheduleVersion] = useState<Node>(
        defaultScheduleVersionId ? scheduleVersionNodeHierarchy?.childNodes.find(({ id }) => id === defaultScheduleVersionId) : scheduleVersionNodeHierarchy?.childNodes[0]
    );
    const [deleteExistingItems, setDeleteExistingItems] = useState<boolean>(false);
    const [file, setFile] = useState<File>();
    const [currentStage, setCurrentStage] = useState<number>(0);

    const isCalendarEvent = typeof eventBlockType.calendarLevel === "number";

    const translate = useIventisTranslate();

    const onEventBlockTypeSelected = (id: string) => {
        const eventBlockType = eventBlockTypes.find((ebt) => ebt.id === id);
        setEventBlockType(eventBlockType);
    };

    const onScheduleVersionSelected = (scheduleVersion: Node) => {
        setScheduleVersion(scheduleVersion);
    };

    const upload = () => {
        if (deleteExistingItems) {
            setShowConfirmation(true);
        } else {
            uploadConfirmed();
        }
    };

    const uploadConfirmed = () => {
        setShowConfirmation(false);
        setUploading(true);
        setCurrentStage(1);

        const reader = new FileReader();

        reader.onload = async () => {
            const csvJson = JSON.stringify(reader.result);

            let errored = false;

            scheduleApi
                .put(
                    `/event-blocks/schedule-versions/${isCalendarEvent ? scheduleVersion.id : rootScheduleVersionId}/event-block-type/${
                        eventBlockType.id
                    }/import?append=${!deleteExistingItems}`,
                    csvJson,
                    {
                        headers: {
                            "Content-Type": "application/json",
                        },
                        customErrorMessage: {
                            title: translate(Content.errors.custom_network_error.generic.title, { action: translate(Content.common.buttons.import) }),
                            message: translate(Content.errors.custom_network_error.generic.message, { action: translate(Content.common.buttons.import) }),
                        },
                    }
                )
                .catch((e) => {
                    const errors = (e.response.data as string).split(",");
                    setErrors(errors);
                    setUploading(false);
                    errored = true;
                })
                .then(() => {
                    if (!errored) {
                        setUploading(false);
                        close(true);
                        toast.success({
                            title: translate(Content.common.upload.import_complete),
                            message: translate(Content.schedule3.eventBlocksImport),
                        });
                    }
                });
        };

        reader.readAsText(file);
    };

    const isValid = () => eventBlockType !== undefined && scheduleVersion !== undefined && file !== undefined;

    const downloadSchedule = () => {
        const baseUrl = getApiBaseUrl(MicroService.SCHEDULE);

        const eventBlockTypeFilter = [
            {
                fieldName: "eventBlockType",
                operator: IventisFilterOperator.Eq,
                value: eventBlockType.name,
            },
        ];

        const params = qs.stringify({ filter: JSON.stringify(eventBlockTypeFilter) }, { arrayFormat: "repeat" });
        const exportUrl = `${baseUrl}event-blocks/schedule-versions/${isCalendarEvent ? scheduleVersion.id : rootScheduleVersionId}/download?${params}`;
        window.open(exportUrl);
    };

    const stages: Stages = [
        {
            primaryButtonText: translate(Content.common.buttons.upload),
            primaryButtonCallback: upload,
            secondaryButtons: [{ buttonText: translate(Content.common.buttons.close), onButtonPressed: () => close() }],
            tertiaryButtonCallback: () => downloadSchedule(),
            tertiaryButtonText: translate(Content.common.buttons.download),
            isValid: isValid(),
        },
        {
            primaryButtonText: translate(Content.common.buttons.back),
            primaryButtonCallback: () => {
                setCurrentStage(0);
                setErrors(undefined);
            },
            secondaryButtons: [
                {
                    buttonText: translate(Content.common.buttons.close),
                    onButtonPressed: close,
                },
            ],
            isValid: !uploading,
        },
    ];

    const handleFileSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
        const inputFile = Array.from(e.target.files)[0];
        setFile(inputFile);
    };

    return (
        <>
            <FormWizardTemplate title={translate(Content.schedule2.upload_event_blocks.title)} stages={stages} currentStage={currentStage} isSubmitting={false}>
                {currentStage === 0 && (
                    <StyledFormContent>
                        <StyledFieldLabel>{translate(Content.schedule2.upload_event_blocks.calendar_level)}</StyledFieldLabel>
                        <RadioGroup className="input-margin-bottom" value={eventBlockType?.id} onChange={(event) => onEventBlockTypeSelected(event.target.value)}>
                            {eventBlockTypes
                                // If there are no sub schedules, then we cannot export/import calendar events
                                .filter(
                                    (ebt) =>
                                        (typeof ebt.calendarLevel === "number" && scheduleVersionNodeHierarchy?.childNodes.some((node) => canEdit(node.authorisation))) ||
                                        (typeof ebt.calendarLevel !== "number" && canEdit(scheduleVersionNodeHierarchy.authorisation))
                                )
                                // Sorted in ascending with null values at the end
                                .sort((a, b) => (a.calendarLevel != null ? a.calendarLevel : Infinity) - (b.calendarLevel != null ? b.calendarLevel : Infinity))
                                .map((ebt) => (
                                    <FormControlLabel key={ebt.id} value={ebt.id} control={<Radio />} label={ebt.name} />
                                ))}
                        </RadioGroup>

                        <AutocompleteWithLoading<Node>
                            style={{ flexGrow: "initial" }}
                            className="input-margin-bottom"
                            options={scheduleVersionNodeHierarchy?.childNodes.filter((node) => canEdit(node.authorisation))}
                            label={translate(Content.schedule.schedule)}
                            value={isCalendarEvent ? scheduleVersion : ({ id: "all", name: translate(Content.common.all) } as Node)}
                            onChange={(value) => onScheduleVersionSelected(value)}
                            loadingOptions={false}
                            getOptionSelected={(option, value) => option === value}
                            getOptionLabel={(option: Node) => option.name}
                            disabled={!isCalendarEvent}
                        />

                        <Button color="primary" component="label" variant={file === undefined ? "contained" : "outlined"}>
                            {file ? <span>{translate(Content.common.upload.change_csv)}</span> : <span>{translate(Content.common.upload.select_csv)}</span>}
                            <input type="file" hidden accept=".csv" onChange={handleFileSelected} />
                        </Button>

                        <FormControlLabel
                            style={{ marginLeft: labelMargin }}
                            control={
                                <Checkbox
                                    value={deleteExistingItems}
                                    onChange={(e) => {
                                        setDeleteExistingItems(e.target.checked);
                                    }}
                                />
                            }
                            label={translate(Content.schedule2.upload_event_blocks.delete_existing)}
                        />
                    </StyledFormContent>
                )}
                {currentStage === 1 && (
                    <>
                        {uploading && <LoadingComponent />}
                        {!uploading && errors && (
                            <>
                                <StyledFieldLabel>{translate(Content.schedule2.upload_event_blocks.csv_validation_errors_subtitle)}</StyledFieldLabel>
                                <StyledErrorsContainer>
                                    <table>
                                        {errors.map((error) => (
                                            <tr>
                                                {/* Column name errors are returned without line numbers */}
                                                {error.split(": ").length === 1 ? (
                                                    <td>{error}</td>
                                                ) : (
                                                    <>
                                                        {/* Assume other errors with the colon and space have line numbers */}
                                                        <td className="line-number">{error.split(": ")[0]}</td>
                                                        <td>{error.split(": ")[1]}</td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                    </table>
                                </StyledErrorsContainer>
                            </>
                        )}
                    </>
                )}
            </FormWizardTemplate>
            <ConfirmationDialogComponent
                show={showConfirmation}
                title={translate(Content.schedule2.upload_event_blocks.delete_existing_confirmation_title)}
                message={translate(Content.schedule2.upload_event_blocks.delete_existing_confirmation_description)}
                confirmText={translate(Content.common.buttons.confirm)}
                cancelText={translate(Content.common.buttons.cancel)}
                handleCancel={() => setShowConfirmation(false)}
                handleConfirm={() => uploadConfirmed()}
            />
        </>
    );
};

const StyledErrorsContainer = styled.div`
    border: 1px solid ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
    flex-grow: 1;
    padding: 0.8rem;
    border-radius: 4px;
    overflow-y: auto;

    td.line-number {
        padding-right: 0.8rem;
        color: ${({ theme }: { theme: Theme }) => theme.typographyColors.subdued};
        font-style: italic;
        vertical-align: top;
        white-space: nowrap;
    }
`;

const StyledFormContent = styled.div`
    ${muiInputFormsCSS}

    .schedule-selector-row {
        display: flex;
        justify-content: stretch;

        button {
            margin-left: 0.8rem;
        }
    }
`;
